import { useMemo } from 'react'

import { AstUndoRedoManager, ContentAST, createSelection, createUndoRedoManager } from '../../../transforms/ast'

/**
 * Per-file undo managers behind the editor tabs. The editor remounts on every
 * file switch, and it builds its undo manager at mount — so without this, each
 * switch away threw the file's history stack out. Holding the manager here
 * keeps every open tab's undo/redo alive for as long as the tab is.
 *
 * Session-only, like the content cache: the stacks are far too large to persist.
 */
export interface UndoHistoryCache {
  /**
   * The manager for `filename`, seeded from `ast` if there isn't a usable one.
   *
   * A cached manager is reused only while its AST is still the exact object the
   * editor is about to mount on. The editor hands the same AST object to
   * `onChange` that it stores on the manager, so identity holds across local
   * edits and breaks precisely when the content came from somewhere else (a
   * revalidation refetch, an out-of-band section save) — the cases where the
   * old history no longer describes the document in front of the author.
   */
  acquire: (filename: string, ast: ContentAST) => AstUndoRedoManager
  /** Drop one file's history (tab closed, file deleted). */
  remove: (filename: string) => void
  /** Follow a file through a rename so its history isn't stranded. */
  rename: (oldFilename: string, newFilename: string) => void
  /** Drop every history except the given files' (tab eviction at the cap). */
  retainOnly: (filenames: string[]) => void
}

export function useUndoHistoryCache(enabled: boolean): UndoHistoryCache | undefined {
  return useMemo(() => {
    if (!enabled) {
      return undefined
    }
    const managers = new Map<string, AstUndoRedoManager>()
    return {
      acquire: (filename, ast) => {
        const cached = managers.get(filename)
        if (cached && cached.getCurrentAST() === ast) {
          return cached
        }
        const manager = createUndoRedoManager(ast, createSelection(ast.blocks[0]?.id || 'def01', 0))
        managers.set(filename, manager)
        return manager
      },
      remove: (filename) => {
        managers.delete(filename)
      },
      rename: (oldFilename, newFilename) => {
        const manager = managers.get(oldFilename)
        if (!manager) {
          return
        }
        managers.delete(oldFilename)
        managers.set(newFilename, manager)
      },
      retainOnly: (filenames) => {
        const keep = new Set(filenames)
        for (const filename of Array.from(managers.keys())) {
          if (!keep.has(filename)) {
            managers.delete(filename)
          }
        }
      },
    }
  }, [enabled])
}
